// Revenue triage — shared toolbar shell for the Income and Expenses tabs.
// Layout only: a primary row (search / property / date slots), a Filters
// button that tucks the secondary controls into a popover with an
// active-count badge, and a right side holding one primary action plus an
// overflow ⋯ menu. All state and URL params stay in the owning tab.

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal, SlidersHorizontal } from "lucide-react";

export function FinancialsToolbar({
  idPrefix,
  primaryFilters,
  advancedFilters,
  activeFilterCount,
  onClearFilters,
  primaryAction,
  overflowItems,
}: {
  /** "expenses" | "income" — namespaces the testids. */
  idPrefix: string;
  /** Always-visible controls: search, property, date preset. */
  primaryFilters: React.ReactNode;
  /** Secondary controls rendered inside the Filters popover. */
  advancedFilters: React.ReactNode;
  /** How many advanced filters are active — shown as a badge on the button. */
  activeFilterCount: number;
  onClearFilters?: () => void;
  /** The single prominent button (e.g. Add Expense). */
  primaryAction?: React.ReactNode;
  /** DropdownMenuItem elements for the ⋯ menu (export, import, tools…). */
  overflowItems?: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`toolbar-${idPrefix}`}>
      {primaryFilters}

      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="gap-1.5" data-testid={`button-${idPrefix}-filters`}>
            <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden="true" />
            Filters
            {activeFilterCount > 0 && (
              <Badge
                variant="secondary"
                className="bg-brand/10 text-brand border-transparent px-1.5 py-0 text-xs"
                data-testid={`badge-${idPrefix}-filter-count`}
              >
                {activeFilterCount}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 space-y-3" align="start" data-testid={`popover-${idPrefix}-filters`}>
          {advancedFilters}
          {onClearFilters && activeFilterCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={onClearFilters}
              data-testid={`button-${idPrefix}-clear-filters`}
            >
              Clear filters
            </Button>
          )}
        </PopoverContent>
      </Popover>

      <div className="ml-auto flex items-center gap-2">
        {primaryAction}
        {overflowItems && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" className="h-9 w-9" aria-label="More actions" data-testid={`button-${idPrefix}-overflow`}>
                <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              {overflowItems}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}
